import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { createPartVendor, fetchPartVendors } from '../../api/partVendor';
import {
  PartVendor,
  partVendorTradeStatusLabel,
} from '../../types/partVendor';
import { notifyError } from '../../utils/notify';
import './CompanyInventoryPickerModal.css';

type StatusFilter = 'ALL' | 'ACTIVE' | 'SUSPENDED';

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'ALL', label: '전체' },
  { value: 'ACTIVE', label: '거래중' },
  { value: 'SUSPENDED', label: '거래중단' },
];

type Props = {
  open: boolean;
  vendors: PartVendor[];
  title?: string;
  onClose: () => void;
  onSelect: (vendor: PartVendor) => void;
  onListsUpdated?: (vendors: PartVendor[]) => void;
};

function vendorPhone(row: PartVendor) {
  return row.mobile?.trim() || row.phone?.trim() || '';
}

function PartVendorPickerModal({
  open,
  vendors,
  title = '발주 업체 선택',
  onClose,
  onSelect,
  onListsUpdated,
}: Props) {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [keyword, setKeyword] = useState('');
  const [selectingId, setSelectingId] = useState<number | null>(null);
  const [showQuick, setShowQuick] = useState(false);
  const [quickName, setQuickName] = useState('');
  const [quickContact, setQuickContact] = useState('');
  const [quickPhone, setQuickPhone] = useState('');
  const [quickFax, setQuickFax] = useState('');
  const [quickSaving, setQuickSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setKeyword('');
      setSelectingId(null);
      setShowQuick(false);
      setQuickName('');
      setQuickContact('');
      setQuickPhone('');
      setQuickFax('');
      setStatusFilter('ALL');
      return;
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && selectingId == null && !quickSaving) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, selectingId, quickSaving]);

  const filtered = useMemo(() => {
    const byStatus =
      statusFilter === 'ALL'
        ? vendors
        : vendors.filter((row) => (row.tradeStatus || 'ACTIVE') === statusFilter);
    const q = keyword.trim().toLowerCase();
    if (!q) return byStatus;
    return byStatus.filter((row) => {
      const hay = [
        row.vendorName,
        row.contactName,
        row.phone,
        row.mobile,
        row.fax,
        row.businessNumber,
        partVendorTradeStatusLabel(row.tradeStatus),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [vendors, statusFilter, keyword]);

  const pickVendor = async (vendor: PartVendor) => {
    try {
      setSelectingId(vendor.vendorId);
      onSelect(vendor);
      onClose();
    } finally {
      setSelectingId(null);
    }
  };

  const handleQuickCreate = async () => {
    if (!quickName.trim()) {
      notifyError('업체명을 입력해 주세요.');
      return;
    }
    try {
      setQuickSaving(true);
      const created = await createPartVendor({
        vendorName: quickName.trim(),
        contactName: quickContact.trim() || undefined,
        phone: quickPhone.trim() || undefined,
        fax: quickFax.trim() || undefined,
      });
      const next = await fetchPartVendors();
      onListsUpdated?.(next);
      const resolved = next.find((row) => row.vendorId === created.vendorId) ?? created;
      onSelect(resolved);
      onClose();
    } catch (err) {
      notifyError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '업체 등록에 실패했습니다.'
      );
    } finally {
      setQuickSaving(false);
    }
  };

  if (!open) return null;

  const busy = selectingId != null || quickSaving;

  return createPortal(
    <div
      className="inv-picker-modal__backdrop"
      role="presentation"
      onClick={() => {
        if (!busy) onClose();
      }}
    >
      <div
        className="inv-picker-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="vendor-picker-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="inv-picker-modal__head">
          <div>
            <p className="inv-picker-modal__eyebrow">등록 업체</p>
            <h3 id="vendor-picker-modal-title">{title}</h3>
            <p className="inv-picker-modal__sub">
              업체를 선택하면 담당자·연락처가 자동으로 채워집니다. 없으면 빠른 등록으로 추가하세요.
            </p>
          </div>
          <button
            type="button"
            className="inv-picker-modal__close"
            aria-label="닫기"
            disabled={busy}
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="inv-picker-modal__filters">
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`inv-picker-modal__chip${statusFilter === opt.value ? ' is-active' : ''}`}
              disabled={busy}
              onClick={() => setStatusFilter(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className="inv-picker-modal__toolbar">
          <input
            type="search"
            className="inv-picker-modal__search"
            placeholder="업체명·담당자·연락처로 검색"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            disabled={busy}
          />
          <span className="inv-picker-modal__count">
            {filtered.length}/{vendors.length}건
          </span>
          <button
            type="button"
            className="inv-picker-modal__quick-toggle"
            disabled={busy}
            onClick={() => setShowQuick((v) => !v)}
          >
            {showQuick ? '빠른 등록 닫기' : '빠른 등록'}
          </button>
        </div>

        {showQuick ? (
          <div className="inv-picker-modal__quick">
            <input
              placeholder="업체명 *"
              value={quickName}
              disabled={busy}
              onChange={(e) => setQuickName(e.target.value)}
            />
            <input
              placeholder="담당자"
              value={quickContact}
              disabled={busy}
              onChange={(e) => setQuickContact(e.target.value)}
            />
            <input
              placeholder="연락처"
              value={quickPhone}
              disabled={busy}
              onChange={(e) => setQuickPhone(e.target.value)}
            />
            <input
              placeholder="Fax"
              value={quickFax}
              disabled={busy}
              onChange={(e) => setQuickFax(e.target.value)}
            />
            <button
              type="button"
              className="inv-picker-modal__quick-submit"
              disabled={busy}
              onClick={() => void handleQuickCreate()}
            >
              {quickSaving ? '등록 중…' : '등록 후 선택'}
            </button>
          </div>
        ) : null}

        <div className="inv-picker-modal__body">
          {vendors.length === 0 ? (
            <p className="inv-picker-modal__empty">
              등록된 업체가 없습니다. 「빠른 등록」으로 추가하세요.
            </p>
          ) : filtered.length === 0 ? (
            <p className="inv-picker-modal__empty">검색 결과가 없습니다.</p>
          ) : (
            <table className="inv-picker-modal__table">
              <thead>
                <tr>
                  <th>업체명</th>
                  <th style={{ width: 100 }}>담당자</th>
                  <th style={{ width: 120 }}>연락처</th>
                  <th style={{ width: 100 }}>Fax</th>
                  <th style={{ width: 80 }}>상태</th>
                  <th style={{ width: 88 }} />
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr
                    key={row.vendorId}
                    className={selectingId === row.vendorId ? 'is-busy' : undefined}
                    onClick={() => {
                      if (!busy) void pickVendor(row);
                    }}
                  >
                    <td>{row.vendorName || '-'}</td>
                    <td>{row.contactName?.trim() || '-'}</td>
                    <td>{vendorPhone(row) || '-'}</td>
                    <td>{row.fax?.trim() || '-'}</td>
                    <td>{partVendorTradeStatusLabel(row.tradeStatus)}</td>
                    <td>
                      <button
                        type="button"
                        className="inv-picker-modal__pick"
                        disabled={busy}
                        onClick={(e) => {
                          e.stopPropagation();
                          void pickVendor(row);
                        }}
                      >
                        {selectingId === row.vendorId ? '선택 중…' : '선택'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default PartVendorPickerModal;
